/* eslint-disable react/no-unused-prop-types */
import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import DialogActions from '@material-ui/core/DialogActions'

import { handleOnConnect, handleHMR, useConnect } from '../virtusa-redux'
import { BaseDialog, ThemeButton } from '../components/Controls'
import { generalDialogThemeOverride } from '../themes'

const reducerKey = 'generalDialog'
const dialogId = 'draggable-generaldialog-title'
const defaultButtonVariant = 'text'
const defaultButtonColor = 'primary'
const defaultButtonSize = 'medium'

// Define the redux initial state at the reducer key
const initialState = {
  id: reducerKey,
  title: '',
  isOpen: false,
  message: '',
  cancelText: 'dialogs.cancel',
  okText: 'dialogs.ok',
  onCancel: () => {},
  onOk: () => {},
  buttons: null,
  disableOkButton: false,
  okButtonVariant: defaultButtonVariant,
  okButtonColor: defaultButtonColor,
  okButtonSize: defaultButtonSize,
  cancelButtonVariant: defaultButtonVariant,
  cancelButtonColor: defaultButtonColor,
  cancelButtonSize: defaultButtonSize,
  closeX: false,
  dividers: false,
  translateMessage: false,
  noTranslateTitle: false,
  noTranslateButtons: false,
  containedButtons: false,
  paperCustomStyles: {},
  primaryTitle: false,
  draggable: false,
}

let { setState, getState } = handleHMR(module, initialState)

const onConnect = store =>
  ({ setState, getState } = handleOnConnect(store, { reducerKey, initialState }))

export const generalDialogSelector = () =>
  getState()

// See initialState for the options.
export const openGeneralDialog = arg => {
  if (typeof arg === 'string') {
    arg = { message: arg }
  }

  setState({ ...arg, isOpen: true })
}

export const closeGeneralDialog = () => {
  setState(initialState)
}

/**
 * GeneralDialog component is a dialog used to display information to the user and give them choices based on that information.
 *
 * @component
 * @returns {GeneralDialog} GeneralDialog component
 */
export default function GeneralDialog() {
  const containedButton = 'contained'
  useConnect(onConnect)
  const dialogObj = useSelector(generalDialogSelector)

  const handleCancel = () => {
    closeGeneralDialog()
    dialogObj.onCancel && dialogObj.onCancel() 
  }

  const handleOk = () => {
    closeGeneralDialog()
    dialogObj.onOk && dialogObj.onOk() 
  }
  
  const DialogButtons = () => {
    if (dialogObj.buttons) {
      return dialogObj.buttons
    }
    return (
      <DialogActions>
        <ThemeButton id={'EditTemplate_cancelText'} noTranslate={dialogObj.noTranslateButtons} size={dialogObj.cancelButtonSize} color={dialogObj.cancelButtonColor} variant={dialogObj.containedButtons ? containedButton : dialogObj.cancelButtonVariant} autoFocus onClick={handleCancel}>
          {dialogObj.cancelText}
        </ThemeButton>
        <ThemeButton id={'EditTemplate_okText'} noTranslate={dialogObj.noTranslateButtons} size={dialogObj.okButtonSize} color={dialogObj.okButtonColor} variant={dialogObj.containedButtons ? containedButton : dialogObj.okButtonVariant} onClick={handleOk} disabled={dialogObj?.disableOkButton}>
          {dialogObj.okText}
        </ThemeButton>
      </DialogActions>
    )
  }

  if (!dialogObj.isOpen) {
    return <></>
  }

  return (
    <BaseDialog
      ref={dialogObj.ref}
      id={dialogObj.id}
      isOpen={dialogObj.isOpen}
      dialogId={dialogId}
      message={dialogObj.message}
      maxWidth={dialogObj.maxWidth}
      handleCancel={handleCancel}
      themeOverride={generalDialogThemeOverride}
      title={dialogObj.title}
      buttons={DialogButtons}
      closeX={dialogObj.closeX}
      dividers={dialogObj.dividers}
      translateMessage={dialogObj.translateMessage}
      noTranslateTitle={dialogObj.noTranslateTitle}
      noTranslateButtons={dialogObj.noTranslateButtons}
      paperCustomStyles={dialogObj.paperCustomStyles}
      buttonsPlacement={dialogObj.buttonsPlacement}
      primaryTitle={dialogObj.primaryTitle}
      draggable={dialogObj.draggable}
    />
  )
}

GeneralDialog.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Indicates whether the dialog should be open.
   */
  isOpen: PropTypes.bool,
  /**
   Dialog title.
   */
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func
  ]),
  /**
   Dialog message.
   */
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func
  ]),
  /**
   Override the text for the cancel button.
   */
  cancelText: PropTypes.string,
  /**
   Override the text for the ok button.
   */
  okText: PropTypes.string,
  /**
   Execute onOk when the ok button is clicked and close the dialog.
   */
  onOk: PropTypes.func,
  /**
   Execute onCancel when the cancel button is clicked and close the dialog.
   */
  onCancel: PropTypes.func,
  /**
   Override the default buttons for the dialog.
   */
  buttons: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func
  ]),
  /**
   Handle dialog Ok button disable state
   */
  disableOkButton: PropTypes.bool,
  /**
   Both buttons default to text, this causes both buttons to be contained.
   */
  containedButtons: PropTypes.bool,
  /**
   Variant for the ok button.
   */
  okButtonVariant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Color for the ok button.
   */
  okButtonColor: PropTypes.oneOf(['primary', 'secondary', '']),
  /**
   Size for the ok button.
   */
  okButtonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Variant for the cancel button.
   */
  cancelButtonVariant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Color for the cancel button.
   */
  cancelButtonColor: PropTypes.oneOf(['primary', 'secondary', '']),
  /**
   Size for the cancel button.
   */
  cancelButtonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Use a close button at the top right of the dialog.
   */
  closeX: PropTypes.bool,
  /**
   Provide dividers for the message content.
   */
  dividers: PropTypes.bool,
  /**
   Translate the message (Must be a string).
   */
  translateMessage: PropTypes.bool,
  /**
   Prevents the language translation of the title.
   */
  noTranslateTitle: PropTypes.bool,
  /**
   Prevents the language translation of the button text when you override them.
   */
  noTranslateButtons: PropTypes.bool,
  /**
   Exported function from this module.<br/>
   Call this function to display a dialog.<br/>
   Use the above props in an object as a parameter to the function call in order to configure the dialog.<br/>
   */
  openGeneralDialog: PropTypes.func,
  /**
   * Buttons Placement - Default is end
   */
  buttonsPlacement: PropTypes.oneOf(['end', 'center', 'start']),
  /**
   * Makes the dialog draggable.
   */
  draggable: PropTypes.bool,
}

GeneralDialog.defaultProps = {
  isOpen: false,
  buttons: null,
  disbleOkButton: false,
  okButtonVariant: 'text',
  okButtonColor: 'primary',
  okButtonSize: 'medium',
  cancelButtonVariant: 'text',
  cancelButtonColor: 'primary',
  cancelButtonSize: 'medium',
  okText: 'dialogs.ok',
  cancelText: 'dialogs.cancel',
  closeX: false,
  dividers: false,
  translateMessage: false,
  noTranslateTitle: false,
  noTranslateButtons: false,
  paperCustomStyles: {},
  buttonsPlacement: 'end',
  maxWidth: ''
}

